import apiClient from '../client';
import { ApiUrls } from '../urls';

export type GDriveFolder = {
    id: string;
    name: string;
    mimeType: string | null;
    folderCount: number | null;
    fileCount: number | null;
    fullPath?: string;
    parentId?: string;
    folders?: GDriveFolder[];
    files?: GDriveFile[];
};

export type GDriveFile = {
    id: string;
    name: string;
    mimeType: string;
};

export type AuthStatusResponse = {
    authenticated: boolean;
};

export type AuthUrlResponse = {
    url: string;
};

export type OAuthCallbackResponse = {
    message: string;
    userId: string;
};

export type FoldersResponse = {
    folders: GDriveFolder[];
    files: GDriveFile[];
    folderCount: number | null;
    fileCount: number | null;
    id: string;
    name: string | null;
    fullPath: string;
    parentId: string | null;
    mimeType: string | null;
};

class GoogleDriveService {
        /**
         * Disconnect the Google Drive folder for a project (set folderId to null)
         */
        async disconnectProjectFolder(projectId: string): Promise<void> {
            try {
                await apiClient.put(
                    ApiUrls.setProjectGdriveFolder(projectId, ''), // send 'null' string for folderId
                );
            } catch (error: any) {
                throw this.handleError(error);
            }
        }
    /**
     * Check if user is authenticated with Google Drive
     */
    async checkAuthStatus(): Promise<AuthStatusResponse> {
        try {
            const response = await apiClient.get<AuthStatusResponse>(ApiUrls.gdriveAuthStatus);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get Google OAuth authorization URL
     */
    async getAuthUrl(redirectUrl: string): Promise<AuthUrlResponse> {
        try {
            const response = await apiClient.get<AuthUrlResponse>(
                ApiUrls.gdriveAuthUrl(redirectUrl)
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Complete OAuth flow by exchanging code for tokens
     */
    async completeOAuthCallback(code: string, redirectUrl: string): Promise<OAuthCallbackResponse> {
        try {
            const response = await apiClient.post<OAuthCallbackResponse>(
                ApiUrls.gdriveOAuthCallback(code, redirectUrl)
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get folders from Google Drive
     * @param folderId - The folder ID to fetch, or 'root' for the root folder
     */
    async getFolders(folderId: string = 'root'): Promise<FoldersResponse> {
        try {
            const response = await apiClient.get<FoldersResponse>(
                ApiUrls.gdriveFolders(folderId)
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Set the Google Drive folder for a project
     */
    async setProjectFolder(projectId: string, folderId: string): Promise<void> {
        try {
            await apiClient.put(
                ApiUrls.setProjectGdriveFolder(projectId, folderId)
            );
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Trigger sync for project's Google Drive folder (fire-and-forget)
     */
    syncProjectFolder(projectId: string): void {
        // Fire and forget - don't wait for completion, don't handle errors
        apiClient.put(
            ApiUrls.syncProjectGdriveFolder(projectId),
            {},
            {
                timeout: 0, // No timeout
            }
        ).catch((error) => {
            console.error('Sync request failed:', error);
            // Silently fail - polling will handle status
        });
    }

    /**
     * Get sync progress for project's Google Drive folder
     * @returns Object with progress string in format "current/total" or null if no sync in progress
     */
    async getSyncProgress(projectId: string): Promise<{ progress: string | null; totalSynced: number }> {
        try {
            const response = await apiClient.get<{ progress: string | null; totalSynced: number }>(
                ApiUrls.gdriveSyncProgress(projectId)
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Handle API errors consistently
     */
    private handleError(error: any): any {
        if (error.response) {
            return {
                message: error.response.data?.message || 'Request failed',
                errors: error.response.data?.errors,
                status: error.response.status,
            };
        }

        // Network error
        return {
            message: 'Network error. Please check your connection.',
            status: 0,
        };
    }
}

export const googleDriveService = new GoogleDriveService();
export default googleDriveService;
